import { QueryClient, type QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';
import { type ApiErrorResult, type ApiResult } from '../types';
import { logApiError } from './errorInterceptor';

export interface UseApiMutationParams<TVariables = void, TResponse = object, TData = TResponse> {
    invalidateQueryOnMutate?: false;
    mutationFn: (variables: TVariables) => Promise<ApiResult<TResponse>>;
    onMutate?: (variables: TVariables, context: unknown) => void;
    transform?: (response: TResponse, variables: TVariables) => TData;
    onError?: (error: ApiErrorResult) => void;
    onManualCacheUpdate?: (
        queryClient: QueryClient,
        response: TData,
        args: TVariables,
        correlationId?: string
    ) => void | Promise<unknown>;
}

export interface UseApiMutationInvalidateQueryParams<TVariables = void, TResponse = object, TData = TResponse> {
    invalidateQueryOnMutate: true;
    mutationFn: (variables: TVariables) => Promise<ApiResult<TResponse>>;
    onMutate?: (variables: TVariables, context: unknown) => void;
    transform?: (response: TResponse, variables: TVariables) => TData;
    onError?: (error: ApiErrorResult) => void;
    queryKey: string | QueryKey;
}

export const useApiMutation = <TVariables = void, TResponse = void, TData = TResponse>(
    params:
        | UseApiMutationParams<TVariables, TResponse, TData>
        | UseApiMutationInvalidateQueryParams<TVariables, TResponse, TData>
) => {
    const {
        mutationFn,
        transform = (data: TResponse) => data as unknown as TData,
        invalidateQueryOnMutate,
        onError,
    } = params;

    const queryClient = useQueryClient();

    const mutate = useMutation<ApiResult<TData>, ApiErrorResult, TVariables>({
        mutationFn: async (variables: TVariables) => {
            const result = await mutationFn(variables);
            return {
                ...result,
                data: transform(result.data, variables),
            };
        },
        onSuccess: async (result, args) => {
            if (invalidateQueryOnMutate) {
                console.log('Invalidating queries for key:', params.queryKey);
                const queryKey = params.queryKey;
                const normalizedQueryKey = Array.isArray(queryKey) ? queryKey : [queryKey];
                await queryClient.invalidateQueries({ queryKey: normalizedQueryKey });
            } else {
                console.log('Manual cache update after mutation');
                await params.onManualCacheUpdate?.(queryClient, result.data, args, result.correlationId);
            }
        },
        onMutate(variables, context) {
            params.onMutate?.(variables, context);
        },
        onError: (error: ApiErrorResult, variables: TVariables) => {
            console.error('Error mutating query:', error);
            // Log error to Zustand store
            logApiError(error, 'mutation', { variables });
            // Call onError if provided
            onError?.(error);
        },
    });

    return {
        mutate,
        isLoading: mutate.isPending,
        queryClient,
    };
};
